
// Packages
import { defineComponent, PropType } from 'vue';
import { mapState, mapActions } from 'pinia';
import { PortalTarget } from 'portal-vue';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import {
  goToApp,
  getDomainUrl,
  ACCOUNT_URLS,
  DOMAINS_NAMES,
} from '@white-label-helper/switch-app';
import { checkIsLessOneHour } from '@white-label-helper/time-helpers';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';

import {
  required,
  alphaNumSpace,
  notNumber,
  requiredIf,
  email,
  maxLength
} from '@white-label-helper/vuelidate';
import { guessTimezone, format, utcToZonedTime, DATE_TIME_FORMATS } from '@white-label-helper/date-utilities';
import { smoothScroll } from '@white-label-helper/scroll';
import { getCookieDomain } from '@white-label-helper/get-cookie-domain';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

// Constants
import {
  PAYMENT_PROVIDERS,
  CANT_FIND_FLIGHT_ID,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';

// Mixins
import checkoutForm from '@white-label-helper/mixin-checkout-form';

// Stores
import { useMarketingPreferencesStore } from '@white-label-store/marketing-preferences';
import { useUserInfoStore } from '@white-label-store/user-info';
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';

// Types
import type { LocaleMessages } from 'vue-i18n';
import type { Partners } from '@white-label-types/partners';
import type { SerializeOptions as CookieSerializeOptions } from 'cookie';
import type {
  BraintreeConfig,
  StripeConfig,
  CartItem,
  WindcaveConfig,
  AdvamConfig,
} from '@white-label-types/parking-checkout';
import type { UserData } from '@white-label-types/stores';

// Components
import IconMessage from '../icon-message/icon-message.vue';
import ControlButton from '../control-button/control-button.vue';
import TextField from '../text-field/text-field.vue';
import PaymentPolicies from '../payment-policies/payment-policies.vue';
import PaymentGatewayBraintree from '../payment-gateway-braintree/payment-gateway-braintree.vue';
import PaymentGatewayStripe from '../payment-gateway-stripe/payment-gateway-stripe.vue';
import PaymentGatewayWindcave from '../payment-gateway-windcave/payment-gateway-windcave.vue';
import PaymentGatewayAdvam from '../payment-gateway-advam/payment-gateway-advam.vue';
import PaymentMarketingChannels from '../payment-marketing-channels/payment-marketing-channels.vue';
import Checkbox from '../checkbox/checkbox.vue';
import PhoneField from '../phone-field/phone-field.vue';
import FlightLookup from '../flight-lookup/flight-lookup.vue';
import Tooltip from '../tooltip/tooltip.vue';

export default defineComponent({
  name: 'PaymentFormManageBooking',

  components: {
    ControlButton,
    Policies: PaymentPolicies,
    Checkbox: Checkbox,
    PaymentGatewaysSharedBraintree: PaymentGatewayBraintree,
    PaymentGatewaysSharedStripe: PaymentGatewayStripe,
    PaymentGatewaysSharedWindcave: PaymentGatewayWindcave,
    PaymentGatewaysSharedAdvam: PaymentGatewayAdvam,
    IconAlert: () => import('@white-label-icon/icon-alert'),
    IconLockStar: () => import('@white-label-icon/icon-lock-star'),
    IconAskMark: () => import('@white-label-icon/icon-ask-mark'),
    IconMessage,
    TextField,
    PhoneField,
    MarketingChannels: PaymentMarketingChannels,
    PortalTarget,
    FlightLookup,
    Tooltip,
  },

  mixins: [checkoutForm],

  props: {
    paymentsConfig: {
      type: Object as PropType<BraintreeConfig | StripeConfig | WindcaveConfig | AdvamConfig>,
      required: true,
    },

    upgradingOriginalId: {
      type: Boolean,
      default: false,
    },

    paymentsConfigReceived: {
      type: Boolean,
      required: true,
      default: false,
    },

    paymentIntentUpdated: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: '',
    },

    isPriceEqualToOriginalBooking: {
      type: Boolean,
      default: false,
    },

    paymentNeeded: {
      type: Boolean,
      default: false,
    },

    orderPrice: {
      type: Number,
      default: 0,
    },

    userData: {
      type: Object as PropType<{ [key: string]: any }>,
      required: true,
    },

    amountToPay: {
      type: Number,
      required: true,
    },

    cancellationPolicies: {
      type: Array as PropType<
        CartItem['inventory_option']['cancellation_policies']
      >,
      default: () => [],
    },

    amendmentPolicies: {
      type: Array as PropType<
        CartItem['inventory_option']['amendment_policies']
      >,
      default: () => [],
    },

    entryDateTime: {
      type: String,
      default: '',
    },

    isLoungesProduct: {
      type: Boolean,
      default: false,
    },
    searchCriteria: {
      type: Object,
    },

    extendFlow: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  validations() {

    const userDetailsValidations =    {
      first_name: {
        required,
        notNumber,
      },
      last_name: {
        required,
        notNumber,
      },
      license_plate: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('plate_number');
        }),
        alphaNumSpace(val: string) {
          // Fix when an empty field is considered an error
          // We have a separate validation to check if this field is required
          if (val) {
            return alphaNumSpace(val);
          }
          return true;
        },
      },
      phone: {
        phone_number: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore - `this` is not recognized in its context
            return (
              this.checkoutForm_checkIfFieldRequired('phone_number') ||
              // @ts-ignore - `this` is not recognized in its context
              ( this.userDetails.marketing_channels?.sms && this.checkoutForm_checkFieldVisibility('phone_number') )
            );
          }),

          isValid(): boolean {
            // @ts-ignore - `this` is not recognized in its context
            const isEmpty = !this.userDetails.phone?.phone_number?.length;
            // @ts-ignore - `this` is not recognized in its context
            return !isEmpty ? this.phoneValid : true;
          },
        },
      },
      zipcode: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('zipcode');
        }),
      },
      outbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'outbound_flight_number'
          );
        }),
      },
      inbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'inbound_flight_number'
          );
        }),
      },
      ...(this.isBookingPortalFlowEnabled
        ? {
            others_email: {
              email,
            },
            booker_email: {
              required: requiredIf(() => this.isBookingPortalFlowEnabled),
              email,
            },
          }
        : {}),
        vehicle_make: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore - `this` is not recognized in its context
            return this.checkoutForm_checkIfFieldRequired('vehicle_make');
          }),
          maxLength: maxLength(this.vehicleFieldsMaxLength)
        },
        vehicle_model: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore - `this` is not recognized in its context
            return this.checkoutForm_checkIfFieldRequired('vehicle_model');
          }),
          maxLength: maxLength(this.vehicleFieldsMaxLength)
        },
        vehicle_colour: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore - `this` is not recognized in its context
            return this.checkoutForm_checkIfFieldRequired('vehicle_colour');
          }),
          maxLength: maxLength(this.vehicleFieldsMaxLength)
        },
    }

    return {
      userDetails: userDetailsValidations
    }
  }
,

  data() {
    const defaultData = {
      showMarketingPreferences: false,
      buttonLoading: false,
      userDetails: {
        first_name: '',
        last_name: '',
        email: '',
        phone: {
          phone_number: '',
          formatted_number: '',
          country_code: '',
        },
        license_plate: '',

        zipcode: '',
        outbound_flight: '',
        outbound_airline: '',
        outbound_airport: '',
        outbound_terminal: '',
        inbound_flight: '',
        inbound_airline: '',
        inbound_airport: '',
        inbound_terminal: '',
        send_booker_email: false,
        send_customer_email: false,
        send_others_email: false,
        marketing_channels: {},
        vehicle_make: '',
        vehicle_model: '',
        vehicle_colour: '',
      } as UserData,
      phoneValid: false,
      others_email: '',
      booker_email: '',
      showDropdownExpanded: false,
    };

    return defaultData;
  },

  computed: {
    ...mapState(useUserInfoStore, {
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      inboundFlightLookupItems: 'readInboundFlightLookupItems',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
      inboundFlightSelected: 'readInboundFlightLookupSelected',
      findOutboundFlightById: 'findOutboundFlightById',
      findInboundFlightById: 'findInboundFlightById',
      flightLookupLoading: 'readFlightLookupLoading',
    }),
    ...mapState(useMarketingPreferencesStore, {
      readUserMarketingSubscription: 'readUserMarketingSubscription',
      readMarketingPreferences: 'readMarketingPreferences',
    }),
    displayOutboundFlightLookup() {
      return this.checkoutForm_checkFieldVisibility('outbound_flight_number') &&
        (this.searchCriteria.is_flight_selected && this.searchCriteria.flight_number && this.searchCriteria.airline_iata);
    },

    displayBraintreeGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.BRAINTREE &&
        this.paymentsConfigReceived
      );
    },

    displayStripeGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE &&
        this.paymentsConfigReceived
      );
    },

    displayWindcaveGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.WINDCAVE &&
        this.paymentsConfigReceived
      );
    },

    displayAdvamGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.ADVAM &&
        this.paymentsConfigReceived
      );
    },

    formLocked(): boolean {
      return !!this.upgradingOriginalId
    },

    payButtonText(): LocaleMessages | string {
      const baseTranslationString = 'UI.button';
      let translationString = 'payForBooking';

      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'paypal')
        translationString = 'paypal';

      if (!this.paymentNeeded || this.isBookingPortalFlowEnabled)
        translationString = 'updateBooking';

      return this.$t(`${baseTranslationString}.${translationString}`);
    },

    payButtonId(): string {
      let btnId = '';

      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'card')
        btnId = 'GAPaymentSubmitCard';
      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'paypal')
        btnId = 'GAPaymentSubmitPaypal';

      if (!this.paymentNeeded) btnId = 'GAUpdateBooking';

      return this.isPriceEqualToOriginalBooking ? '' : btnId;
    },

    partnerTimeZone(): Partners['poi']['timezone'] {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    userMarketingSubscription() {
      return this.readUserMarketingSubscription;
    },

    partnerMarketingPreferences() {
      return this.readMarketingPreferences;
    },
    vehicleFieldsMaxLength(): number {
      return 25
    },
    displayFlightLookup(): boolean {
      const isParkingProduct = getAppHeroProduct(PRODUCT_TYPES.PARKING) === PRODUCT_TYPES.PARKING;
      const displayFlightLookupForParking = isParkingProduct && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true;
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getAppVariable('is_flight_lookup_enabled')
        && displayFlightLookupForParking
        && (this.checkoutForm_checkFieldVisibility('inbound_flight_number') || this.checkoutForm_checkFieldVisibility('outbound_flight_number'));
    },
    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },
  },

  created(){
    this.userDetails = {
      ...this.userDetails,
      ...this.userData,
      others_email: this.userData.booking_emails?.others_email || '',
      booker_email: this.userData.booking_emails?.booker_email || '',
      send_booker_email:
        this.userData.booking_emails?.send_booker_email || false,
      send_customer_email:
        this.userData.booking_emails?.send_customer_email || false,
      send_others_email:
        this.userData.booking_emails?.send_others_email || false,
      first_name: this.userData.user_first_name,
      last_name: this.userData.user_last_name,
      // @ts-ignore mixin method
      phone: this.checkoutForm_checkFieldVisibility('phone_number')
        ? { ...this.userData.phone }
        : { ...this.userDetails.phone },
      // @ts-ignore mixin method
      license_plate: this.checkoutForm_checkFieldVisibility('plate_number')
        ? this.userData.license_plate
        : '',
      // @ts-ignore mixin method
      zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
        ? this.userData.user_zipcode
        : '',
      // @ts-ignore mixin method
      outbound_flight:
        this.checkoutForm_checkFieldVisibility('outbound_flight_number') ||
        this.isLoungesProduct
          ? this.userData.outbound_flight
          : '',
      // @ts-ignore mixin method
      inbound_flight: this.checkoutForm_checkFieldVisibility(
        'inbound_flight_number'
      )
        ? this.userData.inbound_flight
        : '',
    };

    this.addVehicleFieldsToUserDetails()
  },
  async mounted() {
    const phoneNumberFieldVisible = this.checkoutForm_checkFieldVisibility('phone_number');
    const savedUserMarketingChannels = this.userMarketingSubscription?.marketing_channels;

    if (this.userDetails.marketing_channels) {
      if (this.isMarketingChannelAvailable('email')) {
        this.userDetails.marketing_channels.email =
          !!savedUserMarketingChannels?.email;
      }

      if (this.isMarketingChannelAvailable('sms')) {
        this.userDetails.marketing_channels.sms =
          !!savedUserMarketingChannels?.sms;
      }

      /**
      * Toggles the expansion state of the marketing pref dropdown
      * based on the availability of the phone number field and user preferences.
      */
      if (this.userDetails.marketing_channels) {
        const { sms, email } = this.userDetails.marketing_channels;
        this.showDropdownExpanded = phoneNumberFieldVisible ? !(sms && email) : !email;
      }

      this.showMarketingPreferences = true;
    }
    if (this.displayFlightLookup && this.searchCriteria) {
      if (this.checkoutForm_checkFieldVisibility('inbound_flight_number')) {
        await this.fetchFlightLookupDetails({...this.getSearchCriteria('arrival'), direction: 'arrival' });
      }
      if (this.checkoutForm_checkFieldVisibility('outbound_flight_number')) {
        await this.fetchFlightLookupDetails({...this.getSearchCriteria('departure'), direction: 'departure' });
      }

      this.prefillOutboundFlight();
      this.prefillInboundFlight();
    }
  },

  methods: {
    ...mapActions(useUserInfoStore, {
      fetchUserDetails: 'fetchUserDetails',
      fetchVehicleDetails: 'fetchVehicleDetails',
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeInboundFlightLookup: 'selectInboundFlightLookup',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),
    onFormFieldBlur(field: string): void {
      // @ts-ignore - no types for user details validation object
      this.$v.userDetails[field].$touch();
    },
    isMarketingChannelAvailable(channel: string): boolean {
      return !!this.partnerMarketingPreferences?.marketing_channels?.includes(
        channel
      );
    },

    /**
     * Handles form validation errors by scrolling to the first error field.
     */
    handleFormValidationErrors(): boolean {
      this.$v.$touch();

      if (this.$v.$error) {
        // Scroll to the first field with an error
        smoothScroll('.input-container--error');
        return true;
      }

      return false;
    },

    validateForm() {
      // Validate form and stop if there are errors
      if (this.handleFormValidationErrors()) {
        return;
      }

      if (checkIsLessOneHour(this.entryDateTime, this.partnerTimeZone)) {
        const options = {
          path: '/',
          maxAge: 9999,
        } as CookieSerializeOptions;

        if (process.env.NODE_ENV !== 'production') {
          // if process run on 'localhost' we need to share token in sameSite
          options.sameSite = true;
        } else {
          // if process run on 'AWS' we need to share token in domain
          options.domain = getCookieDomain();
        }

        this.$cookies.set('crossAppError', 'lessOneHour', options);
        goToApp(
          getDomainUrl(DOMAINS_NAMES.ACCOUNT),
          ACCOUNT_URLS.MANAGE_BOOKING
        );
        return;
      }

      if (!this.displayWindcaveGateway) {
        this.buttonLoading = true;
      }

      if (!this.paymentNeeded || this.isBookingPortalFlowEnabled) {
        // @ts-ignore - Mixin method
        this.checkoutForm_handlePaymentCompleted({
          payment_method_id: 'zero',
          merchant_id: 'zero',
        });
        this.buttonLoading = false;
      } else {
        // @ts-ignore
        this.$refs.paymentGateway.submitForm();
      }
    },
    /**
     * Validates the form and displays error messages without submitting.
     * Used by wallet payment methods (Apple Pay, Google Pay) to show validation
     * errors when the form is invalid before they attempt payment authorization.
     */
    showErrorIfFormInvalid(): void {
      this.handleFormValidationErrors();
    },
    /**
     * Displays payment decline error message and reload the widget for Windcave and Advam PSPs
     */
    errorHandler() {
      this.buttonLoading = false;
      this.$emit('error');
      this.handleWidgetReload();
    },
    /**
     * Displays technical error message and reload the widget for Windcave and Advam PSPs
     */
    showTechnicalError(): void {
      this.commitUpdatePreloader('');
      this.buttonLoading = false;
      this.$emit('show-technical-error');
      this.handleWidgetReload();
    },

    /**
     * Cancels the current transaction by resetting the loading state,
     * reloading the payment widget and cancelling booking creation
     * Currently used for Advam when Google Pay / Apple Pay is cancelled by the user
     */
    cancelTransaction(): void {
      this.commitUpdatePreloader('');
      this.buttonLoading = false;
      this.$emit('reloadPaymentWidget');
      this.$emit('cancel-booking');
    },
    /**
     * Handles the payment widget reload for Windcave and Advam PSPs.
     * These PSPs require calling /client-config to obtain a new session ID whenever
     * the price is updated (e.g., discount added/removed) or an error occurs,
     * as their widgets initialize only with a session ID.
     */
    handleWidgetReload(): void {
      if (this.displayWindcaveGateway || this.displayAdvamGateway) {
        this.$emit('reloadPaymentWidget');
      }
    },
    addVehicleFieldsToUserDetails(){
        this.userDetails.vehicle_make = this.checkoutForm_checkFieldVisibility('vehicle_make')
        ? this.userData.vehicle_make
        : ''
        this.userDetails.vehicle_model= this.checkoutForm_checkFieldVisibility('vehicle_model')
        ? this.userData.vehicle_model
        : ''
        this.userDetails.vehicle_colour= this.checkoutForm_checkFieldVisibility('vehicle_colour')
        ? this.userData.vehicle_colour
        : ''
    },
    selectOutboundFlightLookup(flightLookup): void {
      this.userDetails.outbound_flight = flightLookup?.id ?? '';
      this.userDetails.outbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.outbound_airport = flightLookup?.airport ?? '';
      this.userDetails.outbound_terminal = flightLookup?.terminalName ?? '';
      this.storeOutboundFlightLookup(flightLookup);
    },
    selectInboundFlightLookup(flightLookup): void {
      this.userDetails.inbound_flight = flightLookup?.id ?? '';
      this.userDetails.inbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.inbound_airport = flightLookup?.airport ?? '';
      this.userDetails.inbound_terminal = flightLookup?.terminalName ?? '';
      this.storeInboundFlightLookup(flightLookup);
    },
    getSearchCriteria(direction: string): string {
      const date = direction === 'arrival'
        ? this.searchCriteria.date2
        : this.searchCriteria.date1;
      const time = direction === 'arrival'
        ? this.searchCriteria.time2
        : this.searchCriteria.time1;
      const airportCode = this.searchCriteria.airport_code;
      const dateTimeISO = `${date}T${time}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      return {
        datetime: format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc),
        airportCode,
      };
    },
    prefillOutboundFlight(): void {
      const flightWasSelected = this.searchCriteria.is_flight_selected;
      if (flightWasSelected) {
        const iata = this.searchCriteria?.airline_iata;
        const flightNumber = this.searchCriteria?.flight_number;
        const flightId = iata && flightNumber ? `${iata}-${flightNumber}` : null;
        const selectedOutboundFlight = flightId ? this.findOutboundFlightById(flightId) : { id: CANT_FIND_FLIGHT_ID };
        this.selectOutboundFlightLookup(selectedOutboundFlight);
      } else {
        this.selectOutboundFlightLookup({ id: CANT_FIND_FLIGHT_ID });
      }
    },
    prefillInboundFlight(): void {
      if (this.userDetails.inbound_flight) {
        this.selectInboundFlightLookup(this.findInboundFlightById(this.userDetails.inbound_flight));
      }
    },
  },
});
